/**
 * 附件预览条（T09）。
 *
 * 图片显示缩略图，其它显示文件名 + 大小；每个条目可单独移除，移除 / 入场动效来自
 * `src/design/motion.ts` 的预设。所有可见部件（容器 / 条目 / 缩略图 / 移除按钮）
 * 都通过 `useComponentMeta` 登记进 `src/ui/registry.tsx`，并带 390x844 逻辑坐标。
 */

import { AnimatePresence, motion } from 'framer-motion';
import type { ReactElement } from 'react';

import type { Attachment } from '../../core/types';
import { useMotionPresets } from '../../design/motion';
import { buildComponentMeta, useComponentMeta } from '../../ui';
import type { BoxLayout, ComponentMetaInput } from '../../ui';
import styles from './attachments.module.css';
import {
  STRIP_REMOVE_SIZE,
  contentBoxStyle,
  offsetBoxStyle,
  stripItemLayout,
  stripRemoveLayout,
  stripThumbLayout,
} from './layout';
import { formatFileSize } from './validate';

export interface AttachmentStripProps {
  attachments: readonly Attachment[];
  /** 预览条容器（画布坐标）。*/
  layout: BoxLayout;
  /** 移除单个附件。*/
  onRemove: (id: string) => void;
  /** 组件清单里的页面标识。*/
  page?: string;
}

interface StripItemProps {
  attachment: Attachment;
  /** 条目（画布坐标）。*/
  layout: BoxLayout;
  /** 预览条容器（画布坐标），用于换算 DOM 相对位置。*/
  parentLayout: BoxLayout;
  page: string;
  onRemove: (id: string) => void;
}

function StripItem({
  attachment,
  layout,
  parentLayout,
  page,
  onRemove,
}: StripItemProps): ReactElement {
  const presets = useMotionPresets();

  const itemMeta: ComponentMetaInput = {
    id: `attachments.strip.item.${attachment.id}`,
    page,
    role: 'container',
    label: `已选附件：${attachment.name}`,
    layout,
    zIndex: 4,
    notes: '预览条条目；安卓用横向 RecyclerView 的 item',
  };
  const itemRef = useComponentMeta<HTMLDivElement>(buildComponentMeta(itemMeta, 'container'));

  const thumbLayout = stripThumbLayout(layout);
  const thumbRole = attachment.kind === 'image' ? 'icon' : 'container';
  const thumbMeta: ComponentMetaInput = {
    id: `attachments.strip.preview.${attachment.id}`,
    page,
    role: thumbRole,
    label: attachment.kind === 'image' ? `图片缩略图：${attachment.name}` : `文件条目：${attachment.name}`,
    layout: thumbLayout,
    zIndex: 5,
    notes: '图片显示缩略图；其它显示文件名 + 大小',
  };
  const thumbRef = useComponentMeta<HTMLDivElement>(buildComponentMeta(thumbMeta, 'container'));

  const removeLayout = stripRemoveLayout(layout);
  const removeMeta: ComponentMetaInput = {
    id: `attachments.strip.remove.${attachment.id}`,
    page,
    role: 'button',
    label: `移除 ${attachment.name}`,
    layout: removeLayout,
    zIndex: 6,
    notes: `触摸目标 ${STRIP_REMOVE_SIZE}x${STRIP_REMOVE_SIZE}dp（≥44dp）；安卓用 ImageButton`,
  };
  const removeRef = useComponentMeta<HTMLButtonElement>(buildComponentMeta(removeMeta, 'button'));

  return (
    <motion.div
      ref={itemRef}
      className={styles.stripItem}
      data-ui-id={itemMeta.id}
      data-ui-role="container"
      role="listitem"
      style={offsetBoxStyle(layout, parentLayout)}
      variants={presets.listStagger.item}
      initial="hidden"
      animate="visible"
      exit={{ opacity: 0, scale: 0.8 }}
      transition={presets.tap.transition}
    >
      <div
        ref={thumbRef}
        className={attachment.kind === 'image' ? styles.stripThumbWrap : styles.stripFileWrap}
        data-ui-id={thumbMeta.id}
        data-ui-role={thumbRole}
        style={offsetBoxStyle(thumbLayout, layout)}
      >
        {attachment.kind === 'image' && attachment.dataUrl !== undefined ? (
          <img className={styles.stripThumb} src={attachment.dataUrl} alt={attachment.name} />
        ) : (
          <>
            <span className={styles.stripFileName}>{attachment.name}</span>
            <span className={styles.stripFileSize}>{formatFileSize(attachment.size)}</span>
          </>
        )}
      </div>
      <button
        ref={removeRef}
        type="button"
        className={styles.removeButton}
        data-ui-id={removeMeta.id}
        data-ui-role="button"
        aria-label={removeMeta.label}
        style={offsetBoxStyle(removeLayout, layout)}
        onClick={() => {
          onRemove(attachment.id);
        }}
      >
        ✕
      </button>
    </motion.div>
  );
}

/** 已选附件预览条。*/
export function AttachmentStrip({
  attachments,
  layout,
  onRemove,
  page = 'attachments',
}: AttachmentStripProps): ReactElement {
  const presets = useMotionPresets();
  const containerMeta: ComponentMetaInput = {
    id: 'attachments.strip',
    page,
    role: 'list',
    label: '已选附件预览条',
    layout,
    zIndex: 3,
    notes: '横向滚动预览条；安卓用 RecyclerView（horizontal）',
  };
  const containerRef = useComponentMeta<HTMLDivElement>(buildComponentMeta(containerMeta, 'list'));

  return (
    <motion.div
      ref={containerRef}
      className={styles.strip}
      data-ui-id={containerMeta.id}
      data-ui-role="list"
      role="list"
      style={contentBoxStyle(layout)}
      variants={presets.listStagger.container}
      initial="hidden"
      animate="visible"
    >
      <AnimatePresence initial={false}>
        {attachments.map((attachment, index) => (
          <StripItem
            key={attachment.id}
            attachment={attachment}
            layout={stripItemLayout(layout, index)}
            parentLayout={layout}
            page={page}
            onRemove={onRemove}
          />
        ))}
      </AnimatePresence>
    </motion.div>
  );
}